import React, { ReactElement, useState } from 'react';
import { ViewPager } from 'components/view-pager';
import Arrow from 'assets/svg/left.svg';
import NewArrowIcon from 'assets/svg/chevron_left_1.5.svg';
import { LeftBtnWrapper, RightBtnWrapper, PlaceholderWrapper, PlaceholderItem, Wrapper, NewArrowsWrapper, NewArrow } from './styled';
import { ListingCardPlaceholder as Placeholder } from 'components/cards/listing/ListingCardPlaceholder';
import { SlideBtn } from 'ds/components/carousels/styled';
import { useTheme } from 'hooks/useTheme';

export interface CarouselProps {
  hasArrows?: boolean;
  newArrowsUI?: boolean;
  visibleItemsPerSlide?: number;
  hasArrowsWhiteBg?: boolean;
  children: ReactElement[];
  isLoading?: boolean;
  placeholderLength?: number;
  nextClickLimit?: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onScrollLeft?: () => void;
  onScrollRight?: () => void;
}

const Carousel: React.FC<CarouselProps> = props => {
  const {
    hasArrows,
    newArrowsUI,
    visibleItemsPerSlide = 1,
    hasArrowsWhiteBg,
    children,
    isLoading,
    placeholderLength,
    nextClickLimit,
    onScrollLeft,
    onScrollRight,
  } = props;
  const [ activeCarouselSlide, setActiveCarouselSlide ] = useState(0);

  const onPrevClick = () => {
    setActiveCarouselSlide((prev) => prev - 1);
    if (onScrollRight) onScrollRight();
  };

  const onNextClick = () => {
    setActiveCarouselSlide(activeCarouselSlide + 1);
    if (onScrollLeft) onScrollLeft();
  };

  const { isRTL } = useTheme();
  const arrayLength = placeholderLength ? placeholderLength : hasArrows ? 4 : 2;

  const isRightArrowDisplayed =
    hasArrows && nextClickLimit !== 0 && (!nextClickLimit || activeCarouselSlide < nextClickLimit) && activeCarouselSlide !== children.length - 1;
  const isLeftArrowDisplayed = hasArrows && activeCarouselSlide !== 0;

  return (
    <Wrapper
      isLeftArrowDisplayed={isLeftArrowDisplayed}
      isRightArrowDisplayed={isRightArrowDisplayed}
      hasArrowsWhiteBg={hasArrowsWhiteBg}
    >
      {isLoading ? (
        <PlaceholderWrapper>
          {new Array(arrayLength).fill(null).map((_, idx) => (
            <PlaceholderItem key={idx}>
              <Placeholder />
            </PlaceholderItem>
          ))}
        </PlaceholderWrapper>
      ) : (
        <>
          <ViewPager
            slideIndex={activeCarouselSlide}
            onSlideIndexChange={setActiveCarouselSlide}
            verticalAlign="flex-start"
            isRTL={isRTL}
            onSwipeLeft={props.onSwipeLeft}
            onSwipeRight={props.onSwipeRight}
          >
            {children}
          </ViewPager>
          {hasArrows && newArrowsUI ? (
            <NewArrowsWrapper>
              <NewArrow data-auto="left-slide-btn" dir="prev" disabled={activeCarouselSlide === 0} onClick={onPrevClick}>
                <NewArrowIcon />
              </NewArrow>
              <NewArrow data-auto="right-slide-btn" dir="next" disabled={activeCarouselSlide === children.length - visibleItemsPerSlide} onClick={onNextClick}>
                <NewArrowIcon />
              </NewArrow>
            </NewArrowsWrapper>
          ) : null}
          {hasArrows && !newArrowsUI ? (
            <>
              {isLeftArrowDisplayed && (
                <LeftBtnWrapper data-auto="left-slide-btn-wrapper" className="left-slide-btn">
                  <SlideBtn data-auto="left-slide-btn" dir="prev" onClick={onPrevClick}>
                    <Arrow />
                  </SlideBtn>
                </LeftBtnWrapper>
              )}
              {isRightArrowDisplayed && (
                <RightBtnWrapper data-auto="right-slide-btn-wrapper" className="right-slide-btn">
                  <SlideBtn data-auto="right-slide-btn" dir="next" onClick={onNextClick}>
                    <Arrow />
                  </SlideBtn>
                </RightBtnWrapper>
              )}
            </>
          ) : null}
        </>
      )}
    </Wrapper>
  );
};

export default Carousel;
